import {memo, useMemo} from 'react';

import {isUndefined} from 'lodash';

import {PaymentMethodTypes} from '~/shared/consts/paymentConsts';
import {getLocalizationService} from '~/shared/services/localisationService';
import {Payment} from '~/shared/services/apiService/models';
import {ComponentBySize} from '~/shared/components/UserTransactions/shared';

import {DesktopTotalsTable, MobileTotalsTable} from './components';

export type TotalsTableProps = {
  payments: Payment[];
  total: number;
  subsidizedMeals: number;
  nettMeals: number;
  companyAnnouncement?: string;
  isIntelReport: boolean;
  isKlaReport: boolean;
  moneyCardsList: MoneyCard[];
  isTenbisCreditByMoneycardID: (cardId: number) => boolean;
  dateBias: number;
  className?: string;
  tableHeaderCustomColor?: string;
  customTableFooterColor?: string;
};

type MoneyCard = {
  moneycardId: number;
  personalGuestTotal: number;
  businessMeetingGuestTotal: number;
  usageBruto: {
    monthly: number;
  };
  usageNet: {
    monthly: number;
  };
};

const TotalsTable = (props: TotalsTableProps) => {
  const {t} = getLocalizationService();
  const {
    isKlaReport,
    isIntelReport,
    payments,
    total,
    subsidizedMeals,
    nettMeals,
    moneyCardsList,
    isTenbisCreditByMoneycardID,
  } = props;

  const columns = useMemo(() => {
    if (isKlaReport) {
      return [
        {
          name: 'emptyCell',
          label: '',
          hideLabel: true,
          width: 32,
        },
        {
          name: 'paymentMethod',
          label: t('payment_type'),
          grow: false,
          width: 150,
        },
        {
          name: 'cardSuffix',
          label: t('card_details'),
          width: 150,
        },
        {
          name: 'subsidizedMeals',
          label: t('subsidized_meals'),
          width: 150,
        },
        {
          name: 'nettMeals',
          label: t('nett_meals'),
          width: 215,
        },
        {
          name: 'total',
          label: t('total'),
          grow: false,
          width: 80,
        },
      ];
    }

    if (isIntelReport) {
      return [
        {
          name: 'emptyCell',
          label: '',
          hideLabel: true,
          width: 32,
        },
        {
          name: 'paymentMethod',
          label: t('payment_type'),
          grow: false,
          width: 170,
        },
        {
          name: 'cardSuffix',
          label: t('card_details'),
          width: 120,
        },
        {
          name: 'personalGuestTotal',
          label: t('personal_guest'),
          width: 100,
        },
        {
          name: 'businessMeetingGuestTotal',
          label: t('business_guest'),
          width: 100,
        },
        {
          name: 'usageBruto',
          label: t('gross'),
          width: 100,
        },
        {
          name: 'usageNet',
          label: t('nett'),
          width: 100,
        },
        {
          name: 'total',
          label: t('total'),
          grow: false,
          width: 80,
        },
      ];
    }
    return [
      {
        name: 'emptyCell',
        label: '',
        hideLabel: true,
        width: 32,
      },
      {
        name: 'paymentMethod',
        label: t('payment_type'),
        grow: false,
        width: 170,
      },
      {
        name: 'cardSuffix',
        label: t('card_details'),
        width: 495,
      },
      {
        name: 'total',
        label: t('total'),
        grow: false,
        width: 80,
      },
    ];
  }, [t, isKlaReport, isIntelReport]);

  const paymentsWithSummaryRow = useMemo(() => {
    if (isUndefined(payments) || isUndefined(total)) {
      return;
    }

    const getCardPrefix = ({cardSuffix, paymentMethod, paymentId}: Partial<Payment>) => {
      if (paymentMethod === PaymentMethodTypes.APPLE_PAY) {
        return '';
      }
      
      const isTenbisCredit = Boolean(paymentId && isTenbisCreditByMoneycardID(paymentId));

      if (isTenbisCredit) {
        return '';
      }
      const isMoneyCardOrCreditCard =
        paymentMethod === PaymentMethodTypes.MONEY_CARD || paymentMethod === PaymentMethodTypes.CREDIT_CARD;
      
      if (isMoneyCardOrCreditCard) {
        return `xxxx-${cardSuffix}`;
      }
      return '';
    };
    const paymentsWithCardPrefix = payments.map(payment => {
      const relevantMoneycard = moneyCardsList.find(({moneycardId}) => moneycardId === payment.paymentId);
      const isTenbisCredit = Boolean(payment.paymentId && isTenbisCreditByMoneycardID(payment.paymentId));

      return {
        ...payment,
        paymentMethod: isTenbisCredit ? t('tenbis_credit') : payment.paymentMethod,
        cardSuffix: getCardPrefix(payment),
        ...(isKlaReport &&
          relevantMoneycard && {
          subsidizedMeals,
          nettMeals,
        }),
        ...(isIntelReport &&
          relevantMoneycard && {
          personalGuestTotal: relevantMoneycard?.personalGuestTotal,
          businessMeetingGuestTotal: relevantMoneycard?.businessMeetingGuestTotal,
          usageBruto: relevantMoneycard?.usageBruto?.monthly,
          usageNet: relevantMoneycard?.usageNet?.monthly,
        }),
      };
    });
    
    return [
      ...paymentsWithCardPrefix,
      {
        paymentMethod: 'total_charges',
        total,
        isTotalAmountRow: true,
        marked: true,
      },
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [payments, total, isIntelReport, isKlaReport, subsidizedMeals, nettMeals]);

  return (
    <ComponentBySize
      MobileComponent={MobileTotalsTable}
      DesktopComponent={DesktopTotalsTable}
      props={{...props, columns, paymentsWithSummaryRow}}
    />
  );
};
export default memo(TotalsTable);
